import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";

import Menu from "../components/globals/Menu";
import Header from "../components/globals/Header";
import Footer from "../components/globals/Footer";

import CareersHero from "../components/partials/CareersHero";
import CareersFilters from "../components/partials/CareersFilters";
import JobListings from "../components/partials/JobListings";
import Pagination from "../components/partials/Pagination";
import ColoredCtaCards from "../components/partials/ColoredCtaCards";

const Careers = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const selectedCategoriesFromServer = useSelector((state) => state.params?.selected_categories || []);
  const selectedRegionsFromServer = useSelector((state) => state.params?.selected_regions || []);

  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [jobOffers, setJobOffers] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [count, setCount] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSeniorities, setSelectedSeniorities] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchJobOffers = ({
    page = currentPage,
    searchQuery,
    selectedCategories,
    selectedSeniorities,
    selectedRegions,
    selectedContracts,
    scrollToJobListings = true
  } = {}) => {
    setLoading(true);
    sh.get("search/job_offers", {
      params: {
        authenticity_token: authenticityToken,
        query: searchQuery,
        selected_categories: selectedCategories,
        selected_seniorities: selectedSeniorities,
        selected_regions: selectedRegions,
        selected_contracts: selectedContracts,
        page,
      },
    })
      .then((res) => {
        setJobOffers(res?.data?.data);
        setPageCount(res.data?.pagy?.pages);
        setCount(res?.data?.pagy?.count);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
        const jobListingsElement = document.getElementById("job-listings");
        if (jobListingsElement && scrollToJobListings) {
          const offset = 100;
          const elementPosition = jobListingsElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }
      );
  };

  const options = {
    searchQuery,
    selectedCategories,
    selectedSeniorities,
    selectedRegions,
    selectedContracts,
    page: currentPage,
    scrollToJobListings: true
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setCurrentPage(1);
    fetchJobOffers(options);
    updateUrlParams(selectedCategories, selectedSeniorities, selectedRegions, selectedContracts, searchQuery);
  };

  const handleCategoryChange = (newSelectedCategories) => {
    setCurrentPage(1);
    fetchJobOffers({
      ...options,
      selectedCategories: newSelectedCategories
    });
    setSelectedCategories(newSelectedCategories);
  };

  const handleRegionChange = (selectedRegions) => {
    setCurrentPage(1);
    fetchJobOffers({
      ...options,
      selectedRegions: selectedRegions
    });
    setSelectedRegions(selectedRegions);
  };

  const handleContractChange = (selectedContracts) => {
    setCurrentPage(1);
    fetchJobOffers({
      ...options,
      selectedContracts: selectedContracts
    });
    setSelectedContracts(selectedContracts);
  };

  const handleSeniorityChange = (newSelectedSeniorities) => {
    setCurrentPage(1);
    fetchJobOffers({
      ...options,
      selectedSeniorities: newSelectedSeniorities
    });
    setSelectedSeniorities(newSelectedSeniorities);
  };

  const handleCategoryRemove = (categoryId) => {
    setCurrentPage(1);
    const newCategories = selectedCategories.filter(category => category !== categoryId);
    fetchJobOffers({
      ...options,
      selectedCategories: newCategories
    });
    setSelectedCategories(newCategories);
  };

  const handleSeniorityRemove = (seniorityCode) => {
    setCurrentPage(1);
    const newSeniorities = selectedSeniorities.filter(seniority => seniority !== seniorityCode);
    fetchJobOffers({
      ...options,
      selectedSeniorities: newSeniorities
    });
    setSelectedSeniorities(newSeniorities);
  };

  const handleRegionRemove = (regionName) => {
    setCurrentPage(1);
    const newRegions = selectedRegions.filter(region => region !== regionName);
    fetchJobOffers({
      ...options,
      selectedRegions: newRegions
    });
    setSelectedRegions(newRegions);
  };

  const handleContractRemove = (contractName) => {
    setCurrentPage(1);
    const newContracts = selectedContracts.filter(contract => contract !== contractName);
    fetchJobOffers({
      ...options,
      selectedContracts: newContracts
    });
    setSelectedContracts(newContracts);
  };

  const removeAllFilters = () => {
    setCurrentPage(1);
    setSelectedCategories([]);
    setSelectedSeniorities([]);
    setSelectedRegions([]);
    setSelectedContracts([]);
    setSearchQuery("");
    fetchJobOffers();
  };

  const updateUrlParams = (selectedCategories, selectedSeniorities, selectedRegions, selectedContracts, searchQuery) => {
    const queryParams = new URLSearchParams();

    if (searchQuery && searchQuery.trim() !== "") {
      queryParams.set("query", searchQuery);
    }

    if (selectedCategories.length > 0 && categories && categories.length > 0) {
      const categoryCodes = selectedCategories.map(id => getCategoryCodesById(id));
      queryParams.set("categories", categoryCodes);
    }

    if (selectedSeniorities.length > 0) {
      queryParams.set("seniorities", selectedSeniorities);
    }

    if (selectedRegions.length > 0) {
      queryParams.set("regions", selectedRegions);
    }

    if (selectedContracts.length > 0) {
      queryParams.set("contracts", selectedContracts);
    }

    const newUrlBase = window.location.pathname;
    const newUrl = queryParams.toString() ? `${newUrlBase}?${queryParams.toString()}` : newUrlBase;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  const convertCategoryCodeToId = (code, fetchedCategories) => {
    const category = fetchedCategories.find(c => c.code === code);
    return category ? category.id : null;
  };

  const getCategoryCodesById = (id) => {
    const category = categories.find(category => category.id === Number(id));
    return category ? category.code : undefined;
  };


  useEffect(() => {
    sh.get("/categories", {
      authenticity_token: authenticityToken,
    }).then((res) => {
      const fetchedCategories = res?.data?.categories;
      setCategories(fetchedCategories);

      const queryParams = new URLSearchParams(window.location.search);
      const searchQueryFromURL = queryParams.get("query") || "";
      const categoriesFromURL = queryParams.get("categories") ? queryParams.get("categories").split(",").map(decodeURIComponent) : [];
      const senioritiesFromURL = queryParams.get("seniorities") ? queryParams.get("seniorities").split(",").map(decodeURIComponent) : [];
      const regionsFromURL = queryParams.get("regions")
        ? queryParams.get("regions").split(",")
        : selectedRegionsFromServer;
      const contractsFromURL = queryParams.get("contracts") ? queryParams.get("contracts").split(",") : [];

      const categoryIdsFromCodes = categoriesFromURL.map(code => convertCategoryCodeToId(code, fetchedCategories));
      const finalSelectedCategories = categoryIdsFromCodes.length > 0 ? categoryIdsFromCodes : selectedCategoriesFromServer;

      setSelectedCategories(finalSelectedCategories);
      setSelectedSeniorities(senioritiesFromURL);
      setSelectedRegions(regionsFromURL);
      setSelectedContracts(contractsFromURL);
      setSearchQuery(searchQueryFromURL);

      const options = {
        searchQuery: searchQueryFromURL,
        selectedCategories: finalSelectedCategories,
        selectedSeniorities: senioritiesFromURL,
        selectedRegions: regionsFromURL,
        selectedContracts: contractsFromURL,
        scrollToJobListings: !firstLoad
      };

      if (firstLoad) {
        setFirstLoad(false);
      }

      setIsInitialLoad(false);

      fetchJobOffers(options);
    });
  }, [currentPage]);

  useEffect(() => {
    if (window.location.hash === "#redirected=true") {
      enqueueSnackbar(intl.formatMessage({ id: "career_hero.job_not_found" }), { variant: "warning" });

      // Remove the hash from the URL to prevent repeated notifications
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!isInitialLoad) {
      updateUrlParams(selectedCategories, selectedSeniorities, selectedRegions, selectedContracts, searchQuery);
    }
  }, [categories, selectedCategories, selectedSeniorities, selectedRegions, selectedContracts, isInitialLoad]);

  return (
    <div id="careers">
      <Menu />
      <Header />

      <main>
        <CareersHero setSearchQuery={setSearchQuery} handleSearchSubmit={handleSearchSubmit} searchQuery={searchQuery} />
        <CareersFilters
          onCategoryChange={handleCategoryChange}
          onRegionChange={handleRegionChange}
          onContractChange={handleContractChange}
          onSeniorityChange={handleSeniorityChange}
          categories={categories}
          selectedCategories={selectedCategories}
          selectedSeniorities={selectedSeniorities}
          selectedRegions={selectedRegions}
          selectedContracts={selectedContracts}
          handleCategoryRemove={handleCategoryRemove}
          handleSeniorityRemove={handleSeniorityRemove}
          handleRegionRemove={handleRegionRemove}
          handleContractRemove={handleContractRemove}
          removeAllFilters={removeAllFilters}
        />
        <JobListings jobOffers={jobOffers} />
        {pageCount > 1 && <Pagination currentPage={currentPage} setPage={setCurrentPage} pageCount={pageCount} />}
        <ColoredCtaCards />
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
